import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RevenueVsBudgetChart, EbitdaMarginChart, CashFlowChart } from "@/components/Charts";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import { ArrowLeft, Warning } from "@phosphor-icons/react";

export default function EntityView() {
    const { entityId } = useParams();
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        client.get(`/dashboard/entity/${entityId}`).then((r) => setData(r.data));
    }, [entityId]);

    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;
    const s = data.snapshot;
    const f = data.financials;
    const bs = f.balance_sheet;

    return (
        <div>
            <PageHeader
                overline={<><Link to="/" className="hover:text-fp-ink"><ArrowLeft size={11} className="inline" /> Group</Link> · <Link to={`/sector/${data.sector_id}`} className="hover:text-fp-ink">{data.sector_id}</Link></>}
                title={data.scope_label}
                description={`Operating entity · ${data.country_code} · Sector: ${data.sector_id}`}
                period={s.period_label}
            />

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
                    <KpiCard label="Revenue" value={fmtMoney(s.revenue.value_musd, { decimals: 0 })} subValue={`vs Budget: ${fmtPctPlain(s.revenue.budget_variance_pct)}`} delta={s.revenue.yoy_pct} deltaLabel="YoY" trend={s.revenue.trend} testId="entity-kpi-revenue" />
                    <KpiCard label="EBITDA" value={fmtMoney(s.ebitda.value_musd, { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(s.ebitda.margin_pct)}`} delta={s.ebitda.mom_pct} trend={s.ebitda.trend} />
                    <KpiCard label="Net Income" value={fmtMoney(s.net_income.value_musd, { decimals: 0 })} trend={s.net_income.trend} />
                    <KpiCard label="Free Cash Flow" value={fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 })} trend={s.free_cash_flow.trend} />
                    <KpiCard label="ROIC" value={fmtPctPlain(s.roic_pct)} subValue={`ROE: ${fmtPctPlain(s.roe_pct)}`} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5">
                        <AIInsightsPanel context={`entity:${entityId}`} contextLabel={data.scope_label} />
                    </div>
                    <div className="lg:col-span-7 bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Revenue Performance</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Actual · Budget · Prior Year</h3>
                        </div>
                        <RevenueVsBudgetChart periods={data.periods} revenue={f.pnl.revenue} budget={f.pnl.budget_revenue} priorYear={f.pnl.prior_year_revenue} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Profitability</div>
                            <h3 className="font-serif text-2xl text-fp-ink">EBITDA & Margin</h3>
                        </div>
                        <EbitdaMarginChart periods={data.periods} ebitda={f.pnl.ebitda} marginPct={f.pnl.ebitda_margin_pct} />
                    </div>
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Cash</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Cash Flow</h3>
                        </div>
                        <CashFlowChart periods={data.periods} ocf={f.cash_flow.operating_cash_flow} capex={f.cash_flow.capex} fcf={f.cash_flow.free_cash_flow} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Balance Sheet */}
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-4">Balance Sheet · Snapshot</div>
                        <dl className="space-y-3">
                            <BsRow label="Total Assets" value={fmtMoney(bs.total_assets, { decimals: 0 })} />
                            <BsRow label="Total Liabilities" value={fmtMoney(bs.total_liabilities, { decimals: 0 })} />
                            <BsRow label="Equity" value={fmtMoney(bs.equity, { decimals: 0 })} strong />
                            <BsRow label="Cash & Equivalents" value={fmtMoney(bs.cash)} />
                            <BsRow label="Debt" value={fmtMoney(bs.debt)} />
                            <BsRow label="Working Capital" value={fmtMoney(bs.working_capital)} />
                            <BsRow label="Debt / Equity" value={bs.debt_to_equity.toFixed(2)} />
                        </dl>
                    </div>

                    {/* HR & Sales */}
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-4">People & Market</div>
                        <dl className="space-y-3">
                            <BsRow label="Headcount" value={fmtNum(f.hr.headcount)} />
                            <BsRow label="Attrition" value={fmtPctPlain(f.hr.attrition_pct)} />
                            <BsRow label="Revenue / Employee" value={`$${fmtNum(f.hr.revenue_per_employee_usd)}`} />
                            <BsRow label="NPS" value={fmtNum(f.sales_market.nps_score)} />
                            <BsRow label="Market Share" value={fmtPctPlain(f.sales_market.market_share_pct)} />
                            <BsRow label="Pipeline" value={fmtMoney(f.sales_market.pipeline_value_musd)} />
                        </dl>
                    </div>

                    {/* ESG & Risk */}
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-4">ESG & Risk</div>
                        <dl className="space-y-3">
                            <BsRow label="Emissions" value={`${fmtNum(f.esg.emissions_tco2e)} tCO₂e`} />
                            <BsRow label="Emissions Intensity" value={`${f.esg.emissions_intensity_tco2_per_musd} tCO₂/$M`} />
                            <BsRow label="Safety LTIFR" value={f.esg.safety_ltifr.toFixed(2)} />
                            <BsRow label="Female Leadership" value={fmtPctPlain(f.esg.female_leadership_pct)} />
                            <BsRow label="Community Invest." value={fmtMoney(f.esg.community_investment_musd)} />
                        </dl>
                        <div className="mt-5 pt-4 border-t border-fp-border">
                            <div className="flex items-center gap-2 mb-2">
                                <Warning size={14} className="text-fp-terracotta" />
                                <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-secondary">Top Risks · Score {f.risk.risk_score}/5</div>
                            </div>
                            <ul className="space-y-1">
                                {f.risk.top_risks.map((r) => (
                                    <li key={r} className="font-sans text-sm text-fp-secondary">— {r}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const BsRow = ({ label, value, strong }) => (
    <div className={`flex items-baseline justify-between border-b border-fp-border pb-2 ${strong ? "border-b-2 border-fp-ink" : ""}`}>
        <dt className="font-sans text-sm text-fp-secondary">{label}</dt>
        <dd className={`font-mono text-sm ${strong ? "text-fp-ink font-semibold" : "text-fp-ink"}`}>{value}</dd>
    </div>
);
